/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import classnames from 'classnames';
import * as React from 'react';

import { IHeaderViewProps } from '@msdyn365-commerce-modules/header';
import { Collapse, Drawer, Module, Node } from '@msdyn365-commerce-modules/utilities';
import { format } from '@msdyn365-commerce/retail-proxy';
import { IHeaderProps } from '../definition-extensions/header.ext.props.autogenerated';
import { CartIconComponent } from './components/cart-icon.component';
// import { ContactInfoClass } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceModels.g';

/**
 *
 * Header component
 * @extends {React.PureComponent<IHeaderViewProps & IHeaderProps<{}>>}
 */
class HeaderView extends React.Component<IHeaderViewProps & IHeaderProps<{}>> {
    constructor(props: IHeaderViewProps & IHeaderProps<{}>) {
        super(props);
    }

    public componentDidMount(): void {
        this._stickyHeaderSetup();
    }

    public render(): JSX.Element {
        const {
            HeaderTag,
            HeaderContainer,
            HeaderTopBarContainer
        } = this.props;
        return (
            <Module {...HeaderTag}>
                <Node {...HeaderContainer}>
                    <Node {...HeaderTopBarContainer} className={classnames(HeaderTopBarContainer.className, this.props.mobileMenuCollapsed ? '' : 'dropdown-open')}>
                        {this._renderTopBarLeft()}
                        {this._renderTopBarCenter()}
                        {this._renderTopBarRight()}
                    </Node>
                    {this._renderCollapseMenu()}
                    <Node className={'ms-header__desktop-view'}>
                        {this._renderReactFragment(this.props.menuBar)}
                    </Node>
                </Node>
            </Module>
        );
    }

    private _renderTopBarLeft = (): JSX.Element | null => {
        return (
            <div className='ms-header__topbar-left'>
                {this.props.navIcon}
                {this.props.logo}
                {this._renderReactFragment(this.props.search)}
            </div>
        );
    }

    private _renderTopBarRight = (): JSX.Element | null => {
        const {Divider} = this.props;
        return (
            <div className='ms-header__topbar-right'>
                {this._renderReactFragment(this.props.slots.countryPicker)}
                {this._renderReactFragment(this.props.slots.siteOptions)}
                {this._renderAccountBlock(false)}
                {this.props.wishListIconDesktop}
                <Node {...Divider}/>
                <div className='ms-header-cart-icon'>  {this._renderCartIcon()} </div>
            </div>
        );
    }

    private _renderTopBarCenter = (): JSX.Element | null => {
        return (
            <div className='ms-header__topbar-center'>
                {this._renderReactFragment(this.props.slots.cittaLogo)}
            </div>
        );
    }

    private _renderCartIcon = (): JSX.Element | null => {

        if (this.props.data.cart.result) {
            const cartItem = `${this.props.data.cart.result.totalItemsInCart}`;
            const itemCount = cartItem && parseInt(cartItem, 10) || 0;
            let label = this.props.resources.cartLabelNoItems || 'Shopping bag, having no items';
            if (itemCount === 1) {
                label = this.props.resources.cartLabelWithOneItem || 'Shopping bag, having one item';
            } else if (itemCount > 1) {
                label = this.props.resources.cartLabel && format(this.props.resources.cartLabel, cartItem) || `Shopping bag, having ${itemCount} items`;
            }
            return (
                <CartIconComponent
                    cartLabel={label}
                    context={this.props.context}
                    id={this.props.id}
                    typeName={this.props.typeName}
                    data={{ cart: this.props.data.cart.result }}
                />
            );
        } else {
            return null;
        }
    }

    private _renderCollapseMenu = (): JSX.Element | null => {
        const {Divider, MobileMenuLinksContainer, mobileMenuCollapsed} = this.props;
        return (
            <Collapse className={'ms-header__collapsible-hamburger'} isOpen={!mobileMenuCollapsed}>
                <Node {...MobileMenuLinksContainer}>
                    {this._renderReactFragment(this.props.search)}
                    {this._renderMobileAccountBlock(true)}
                    { this.props.wishListIconMobile }
                </Node>
                <Node {...Divider}/>
                {this._renderReactFragment(this.props.menuBar)}
            </Collapse>
        );
    }

    private renderCustomerName = (): React.ReactChild => {
        const { accountInformation } = this.props.data;
        const customer = accountInformation && accountInformation.result;
        return (
            <>{customer ? customer.FirstName || customer.Name : ''}</>
        );
    };

    private _renderMobileAccountBlock = (renderForMobile: boolean): JSX.Element | null => {
        const {
            AccountInfoDropdownParentContainer,
            signOutLink,
            signInLink,
            accountLinks} = this.props;

        if (AccountInfoDropdownParentContainer) {
            const accountClassName = classnames('ms-header__drawer', AccountInfoDropdownParentContainer.className, renderForMobile ? 'account-mobile' : 'account-desktop');

            if (accountLinks) {
                return (
                    <Drawer className={accountClassName} openGlyph='ms-header__drawer-open' closeGlyph='ms-header__drawer-close' glyphPlacement='end' toggleButtonText={this.renderCustomerName()}>
                        <div>
                            { accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false }
                            {signOutLink}
                        </div>
                    </Drawer>
                );
            } else if (signInLink) {
                return (
                    <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                        {signInLink}
                    </Node>
                );
            }
        }
        return null;
    }

    private _renderAccountBlock = (renderForMobile: boolean): JSX.Element | null => {
        const {
            AccountInfoDropdownParentContainer,
            AccountInfoDropdownPopoverConentContainer,
            accountInfoDropdownButton,
            signOutLink,
            signInLink,
            accountLinks,
        } = this.props;

        if (AccountInfoDropdownParentContainer) {
            if (AccountInfoDropdownPopoverConentContainer) {
                return (
                    <Node {...AccountInfoDropdownParentContainer}>
                        {accountInfoDropdownButton}
                        <Node {...AccountInfoDropdownPopoverConentContainer}>
                            { accountLinks ? accountLinks.map((link: React.ReactNode)  => link) : false }
                            {signOutLink}
                        </Node>
                    </Node>
                );
            } else if (signInLink) {
                return (
                    <Node {...AccountInfoDropdownParentContainer}>
                        {signInLink}
                    </Node>
                );
            }
        }
        this.props.context.telemetry.error('Header content is empty, module wont render.');
        return null;
    }

    private _renderReactFragment = (items: React.ReactNode[]): JSX.Element | null => {
        return (
            <React.Fragment>
                {items && items.length
                    ? items.map((slot: React.ReactNode, index: number) => {
                          return <React.Fragment key={index}>{slot}</React.Fragment>;
                      })
                    : null}
            </React.Fragment>
        );
    }

    private _stickyHeaderSetup(): void {
        // Set header update methods to trigger on scroll
        window.onscroll = () => {
            this._updateHeader();
        };
        this._updateHeader();
    }

    private _updateHeader(): void {
        // Get heights of cookie and promotion banners
        const headerAlertsContainer: HTMLElement | null = document.querySelector('.ms-promo-banner');
        const bannerHeights = headerAlertsContainer ? headerAlertsContainer.offsetHeight : 0;

        // triggers opacity change of header
        const headerElem = document.querySelector('.ms-header');
        if (headerElem) {
            if (document.documentElement.scrollTop > bannerHeights) {
                headerElem.classList.add('lock-opaque');
            } else {
                headerElem.classList.remove('lock-opaque');
            }
        }

        // Update sticky header position and opacity
        const stickyHeader: HTMLElement | null = document.querySelector('.ms-header__desktop-view');
        const headerLogo: HTMLElement | null = document.querySelector('.ms-header__topbar-center');
        const headerLeftActionItem: HTMLElement | null = document.querySelector('.ms-header__topbar-left');
        const headerRightActionItem: HTMLElement | null = document.querySelector('.ms-header__topbar-right');

        if (stickyHeader && headerLogo && headerLeftActionItem && headerRightActionItem) {
            // Fix center sticky header
            const navStickyPos = headerLogo.offsetHeight + bannerHeights;
            if (document.documentElement.scrollTop > navStickyPos) {
                stickyHeader.classList.add('fixed');
            } else  {
                stickyHeader.classList.remove('fixed');
            }

            // Fix edge sticky headers
            if (document.documentElement.scrollTop > bannerHeights) {
                headerLeftActionItem.classList.add('fixed');
                headerRightActionItem.classList.add('fixed');
            } else  {
                headerLeftActionItem.classList.remove('fixed');
                headerRightActionItem.classList.remove('fixed');
            }
        }
    }
}

export default HeaderView;
